/**
 * Dark-mode background variants — showcase-selectable pairings spanning the
 * approved dark families only: dark black, navy blue, dark gray (per Kamal,
 * 2026-06-10 — no saturated cobalt surfaces). Light mode is never affected;
 * brand --zom-* neutrals (navbar/chrome) never change. CSS lives in
 * globals.css under `.dark[data-dark-variant="..."]`.
 *
 * Selection defaults to "pure-black" — the approved default pairing, whose
 * override block is identical to the base .dark tokens, so first paint
 * never shifts. An explicit "none" clears the attribute so custom token
 * edits apply without any variant block. The document appearance policy imports
 * these definitions for first paint; stored choices belong only to previews.
 */

export const DARK_MODE_VARIANT_STORAGE_KEY = "zom-preview-dark-variant";

export const DARK_MODE_VARIANTS = [
  {
    id: "pure-black",
    label: "Pure Black",
    description: "True black canvas, neutral dark surfaces, no blue",
    swatches: ["#000000", "#0d0d0d", "#171717"],
  },
  {
    id: "obsidian",
    label: "Obsidian",
    description: "Obsidian canvas, navy-deep chat shell",
    swatches: ["#070707", "#0a1220", "#122032"],
  },
  {
    id: "navy-classic",
    label: "Navy Classic",
    description: "The previous midnight-navy palette",
    swatches: ["#08111f", "#101821", "#182434"],
  },
  {
    id: "black-navy",
    label: "Black Navy",
    description: "Pure black canvas, navy-deep shell",
    swatches: ["#000000", "#0a1220", "#122032"],
  },
  {
    id: "graphite",
    label: "Graphite",
    description: "Dark gray set from the brand graphite family",
    swatches: ["#101010", "#161616", "#232428"],
  },
] as const;

export type DarkModeVariant = (typeof DARK_MODE_VARIANTS)[number];
export type DarkModeVariantId = DarkModeVariant["id"];

/**
 * "none" = no `data-dark-variant` attribute. The base .dark tokens (plus any
 * runtime customization) win, so custom canvas/surface edits are never
 * fought by a variant override block.
 */
export const DARK_VARIANT_NONE = "none";

export type DarkVariantSelection = DarkModeVariantId | typeof DARK_VARIANT_NONE;

export const DEFAULT_DARK_VARIANT_SELECTION: DarkVariantSelection =
  "pure-black";

export function isDarkModeVariantId(value: string): value is DarkModeVariantId {
  return DARK_MODE_VARIANTS.some((variant) => variant.id === value);
}

export function isDarkVariantSelection(
  value: string,
): value is DarkVariantSelection {
  return value === DARK_VARIANT_NONE || isDarkModeVariantId(value);
}

export function applyDarkModeVariant(selection: DarkVariantSelection): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (selection === DARK_VARIANT_NONE) {
    delete root.dataset.darkVariant;
  } else {
    root.dataset.darkVariant = selection;
  }
}
